import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  useDisclosure,
  ButtonGroup,
  Alert,
  AlertTitle,
  AlertDescription,
  VStack,
  AlertIcon,
  Image,
  useToast,
  Tooltip,
} from '@chakra-ui/react'
import * as React from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { getStorageImageUrl } from 'utils/imageUrl'
import OptimizedImage from 'components/image/OptimizedImage'

// Custom components
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlinePlus } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
// Import community-specific modals and hooks
import { useGetCommunities, useDeleteCommunity } from 'queries/community.query'
import { CreateCommunityModal } from './CreateCommunityModal'
import { EditCommunityModal } from './EditCommunityModal'
import { DeleteCommunityModal } from './DeleteCommunityModal'
import { CustomPagination } from './pagination'

const columnHelper = createColumnHelper()

export default function CommunityTable () {
  const [sorting, setSorting] = React.useState([])
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')

  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isEditOpen,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure()
  const {
    isOpen: isCreateOpen,
    onOpen: onOpenCreate,
    onClose: onCloseCreate,
  } = useDisclosure()

  const toast = useToast()

  const [selectedCommunity, setSelectedCommunity] = React.useState(null)
  const [selectedForDeletion, setSelectedForDeletion] = React.useState(null)

  const { mutate: deleteMutate, isPending } = useDeleteCommunity()

  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  })

  const { data, isLoading, isError, refetch } = useGetCommunities(
    pagination.current,
    pagination.pageSize,
  )

  const rows = data?.data || []

  const handleEditOpen = community => {
    setSelectedCommunity(community)
    onOpenEdit()
  }

  const handleSingleDelete = id => {
    setSelectedForDeletion([id])
    onOpen()
  }

  const onDelete = () => {
    deleteMutate(selectedForDeletion, {
      onSuccess: () => {
        onClose()
        setSelectedForDeletion([])
        refetch()
        toast({
          title: 'Community Deleted.',
          description: 'The community has been successfully deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: err => {
        toast({
          title: 'Failed to Delete Community.',
          description:
            err.response?.data?.message ||
            'There was an error deleting the community',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Name
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' fontWeight='700'>
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('description', {
      id: 'description',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Description
        </Text>
      ),
      cell: info => (
        // <Flex justifyContent='center' alignItems='center'>
        //   <Text color={textColor} fontSize='sm'>
        //     {info.getValue()}
        //   </Text>
        // </Flex>
        <Flex justifyContent='center' alignItems='center'>
          <Tooltip label={info.getValue()} placement='top'>
            <Text
              color={textColor}
              fontSize='sm'
              maxW='200px'
              overflow='hidden'
              textOverflow='ellipsis'
              whiteSpace='nowrap'
            >
              {info.getValue()
                ? `${info.getValue().substring(0, 50)}...`
                : 'No Message'}
            </Text>
          </Tooltip>
        </Flex>
      ),
    }),
    columnHelper.accessor('logo', {
      id: 'logo',
      header: () => (
        <Text
          justifyContent='center'
          align='center'
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
        >
          Logo
        </Text>
      ),
      cell: info => {
        const imageUrl = getStorageImageUrl(info.getValue())
        return (
          <Flex justifyContent='center' alignItems='center'>
            {imageUrl ? (
              <OptimizedImage
                src={imageUrl}
                alt='Logo'
                boxSize='50px'
                borderRadius='full'
                objectFit='cover'
              />
            ) : (
              <Text color='gray.400' fontSize='xs'>
                No Logo
              </Text>
            )}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('photo', {
      id: 'photo',
      header: () => (
        <Text
          justifyContent='center'
          align='center'
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
        >
          Photo
        </Text>
      ),
      cell: info => {
        const imageUrl = getStorageImageUrl(info.getValue())
        return (
          <Flex justifyContent='center' alignItems='center'>
            {imageUrl ? (
              <OptimizedImage
                src={imageUrl}
                alt='Photo'
                boxSize='50px'
                borderRadius='md'
                objectFit='cover'
              />
            ) : (
              <Text color='gray.400' fontSize='xs'>
                No Photo
              </Text>
            )}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Flex justifyContent='center' alignItems='center'>
          <ButtonGroup size='sm' isAttached>
            <Button
              colorScheme='brand'
              onClick={() => handleEditOpen(row.original)}
            >
              <EditIcon />
            </Button>
            <Button
              colorScheme='red'
              onClick={() => handleSingleDelete(row.original.id)}
            >
              <AiOutlineDelete />
            </Button>
          </ButtonGroup>
        </Flex>
      ),
    }),
  ]

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) {
    return <SkeletonTable />
  }

  if (isError || !data) {
    return (
      <VStack spacing={4} align='center' mt='20'>
        <Alert status='error' variant='subtle'>
          <AlertIcon />
          <Box>
            <AlertTitle>Error Occurred!</AlertTitle>
            <AlertDescription>
              Unable to load community data. Please try again later.
            </AlertDescription>
          </Box>
        </Alert>
        <Button onClick={refetch} colorScheme='red' isLoading={isLoading}>
          Retry
        </Button>
      </VStack>
    )
  }

  return (
    <Card
      flexDirection='column'
      w='100%'
      px='0px'
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px='25px' mb='8px' justifyContent='space-between' align='center'>
        <Text
          color={textColor}
          fontSize='22px'
          fontWeight='700'
          lineHeight='100%'
        >
          Communities
        </Text>
        <Button
          leftIcon={<AiOutlinePlus />}
          colorScheme='brand'
          size='sm'
          borderRadius='12px'
          onClick={onOpenCreate}
        >
          New Community
        </Button>
      </Flex>
      <Box
        maxH='600px'
        overflowY='auto'
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#999 #ddd' }}
      >
        <Table color='gray.500' mb='24px' mt='12px'>
          <Thead position='sticky' zIndex='1' top='0' bg={TheadBackground}>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    borderColor={borderColor}
                    textAlign='center'
                  >
                    <Flex
                      justifyContent='center'
                      align='center'
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color='gray.400'
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map(row => (
              <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {data && (
        <CustomPagination
          total={data?.total || 0}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onPageChange={page =>
            setPagination(prev => ({ ...prev, current: page }))
          }
          onPageSizeChange={size =>
            setPagination(prev => ({ ...prev, pageSize: size }))
          }
        />
      )}
      <DeleteCommunityModal
        isOpen={isOpen}
        onClose={onClose}
        onDelete={onDelete}
        isLoading={isPending}
      />
      <CreateCommunityModal isOpen={isCreateOpen} onClose={onCloseCreate} />
      <EditCommunityModal
        isOpen={isEditOpen}
        onClose={onCloseEdit}
        community={selectedCommunity}
      />
    </Card>
  )
}
